import React from 'react';
import { Heart, Shield, Users, Home } from 'lucide-react';

const MissionSection = ({ className = "" }) => {
  const missionPoints = [
    {
      icon: Heart,
      title: "Age Gracefully",
      description: "Every individual deserves to age with respect and dignity in a caring environment"
    },
    {
      icon: Shield,
      title: "Independence",
      description: "Fostering independence while providing the support needed for peace of mind"
    },
    {
      icon: Users,
      title: "Friendship",
      description: "Building meaningful connections and lasting friendships within our community"
    },
    {
      icon: Home,
      title: "Family Atmosphere",
      description: "Creating a nurturing atmosphere where every resident is treated like family"
    }
  ];

  return (
    <div className={`pt-20 sm:pt-28 ${className}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-red-50 px-6 py-3 rounded-full text-[var(--primarycolor)] text-sm font-medium mb-8">
            <Heart className="w-4 h-4" />
            Our Mission
          </div>
          
          <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-8 tracking-tight">
            Compassionate Care with
            <span className="block text-[var(--primarycolor)] font-normal">Dignity</span>
          </h2>
          
          {/* <div className="w-32 h-px bg-gradient-to-r from-transparent via-[var(--primarycolor)] to-transparent mx-auto mb-12"></div> */}
        </div>

        {/* Mission Content */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Left - Mission Statement */}
          <div className="relative">
            {/* Background decoration */}
            {/* <div className="absolute -top-4 -left-4 w-24 h-24 bg-red-500/10 rounded-full blur-xl"></div> */}
            
            <div className="relative bg-gradient-to-br from-red-50 via-rose-50 to-white rounded-2xl p-10 border border-gray-200">
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-red-500/10 to-transparent rounded-bl-full"></div>
              <div className="w-16 h-16 bg-[var(--primarycolor)] rounded-2xl flex items-center justify-center mb-8">
                <Heart className="w-8 h-8 text-white" />
              </div>
              
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">Our Mission</h3>
              
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                At Dignity Retirement Homes, we believe every individual deserves to age gracefully and with respect. Our mission is to provide exceptional senior living environments that foster independence, friendship, and peace of mind.
              </p>
              
              <p className="text-lg text-gray-700 leading-relaxed">
                We create a nurturing atmosphere where every resident is treated like a family member, ensuring dignity and compassion in every interaction.
              </p>
            </div>
          </div>

          {/* Right - Mission Points Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {missionPoints.map((point, index) => {
              const IconComponent = point.icon;
              return (
                <div 
                  key={index} 
                  className="group relative bg-gradient-to-br from-red-50 via-rose-50 to-white rounded-xl p-6 border border-red-100 overflow-hidden"
                >
                  {/* Decorative corner */}
                  <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-red-500/10 to-transparent rounded-bl-full"></div>
                  
                  {/* Icon with gradient */}
                  <div className="relative mb-4">
                    <div className="w-12 h-12 bg-[var(--primarycolor)] rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <div className="absolute inset-0 w-12 h-12 bg-gradient-to-br from-red-600 to-rose-700 rounded-xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity"></div>
                  </div>
                  
                  <h4 className="text-lg font-semibold text-gray-900 mb-3 relative">{point.title}</h4>
                  <p className="text-gray-700 text-sm leading-relaxed relative">{point.description}</p>
                  
                  {/* Bottom accent line */}
                  <div className="absolute bottom-0 left-0 w-0 h-1 bg-gradient-to-r from-red-600 to-rose-700 group-hover:w-full transition-all duration-500 rounded-full"></div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mission Impact Statement */}
        <div className="text-center">
          <div className="max-w-3xl mx-auto bg-[var(--primarycolor)] rounded-3xl p-12 text-white relative overflow-hidden">
            {/* Background pattern */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full blur-2xl"></div>
            
            <div className="relative z-10">
              <h3 className="text-2xl md:text-3xl font-light mb-6 leading-tight">
                Building Communities Where
                <span className="block font-normal">Dignity Meets Compassion</span>
              </h3>
              <p className="text-red-50 text-lg leading-relaxed">
                Every decision we make, every space we design, and every service we provide is guided by our unwavering commitment to treating our residents with the dignity, respect, and care they deserve.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MissionSection;